import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Plus, Pencil, Trash2, Calendar, CheckCircle } from "lucide-react"
import { useTranslation } from "react-i18next"
import * as Dialog from "@radix-ui/react-dialog"
import { useForm } from "react-hook-form"
import { X } from "lucide-react"
import { api, ApiRequestError } from "@/lib/api"
import { formatCurrency, formatDate, LOCALE_MAP } from "@/lib/utils"
import { cn } from "@/lib/utils"
import type { PricingResponse, CreatePricingInput } from "@fohet/caravan-booker-shared/types"
import { useAdminAreal } from "@/hooks/useAdminAreal"
import { inputClass, labelClass } from "@/lib/styleClasses"

type PricingFormValues = Omit<CreatePricingInput, "discounts">

const defaultValues: PricingFormValues = {
    name: "",
    description: "",
    validFrom: "",
    validTo: "",
    pricePerNight: 0,
    localTaxPerPersonPerNight: 0,
    petFeePerNight: 0,
    active: true,
}

interface PricingFormModalProps {
    open: boolean
    onClose: () => void
    editing: PricingResponse | null
    arealId: string
    queryKey: unknown[]
}

const PricingFormModal = ({ open, onClose, editing, arealId, queryKey }: PricingFormModalProps) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { register, handleSubmit, reset, formState: { errors } } = useForm<PricingFormValues>({
        defaultValues: editing
            ? {
                name: editing.name,
                description: editing.description ?? "",
                validFrom: editing.validFrom,
                validTo: editing.validTo,
                pricePerNight: editing.pricePerNight / 100,
                localTaxPerPersonPerNight: editing.localTaxPerPersonPerNight / 100,
                petFeePerNight: editing.petFeePerNight / 100,
                active: editing.active,
            }
            : defaultValues,
    })

    const mutation = useMutation({
        mutationFn: (values: PricingFormValues) => {
            const body = {
                name: values.name,
                description: values.description || undefined,
                validFrom: values.validFrom,
                validTo: values.validTo,
                pricePerNight: Math.round(values.pricePerNight * 100),
                localTaxPerPersonPerNight: Math.round(values.localTaxPerPersonPerNight * 100),
                petFeePerNight: Math.round(values.petFeePerNight * 100),
                active: values.active,
            }
            if (editing) {
                return api.patch(`/areals/${arealId}/price-lists/${editing.id}`, body)
            }
            return api.post(`/areals/${arealId}/price-lists`, body)
        },
        onSuccess: () => {
            toast.success(editing ? t("admin.pricing.toastUpdated") : t("admin.pricing.toastCreated"))
            queryClient.invalidateQueries({ queryKey })
            reset(defaultValues)
            onClose()
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.pricing.toastFailed"))
        },
    })

    return (
        <Dialog.Root open={open} onOpenChange={(o) => { if (!o) { reset(defaultValues); onClose() } }}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
                <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-white rounded-[40px] shadow-2xl p-8 outline-none">
                    <div className="flex items-center justify-between mb-6">
                        <Dialog.Title className="text-xl font-black text-slate-900">
                            {editing ? t("admin.pricing.editTitle") : t("admin.pricing.addTitle")}
                        </Dialog.Title>
                        <Dialog.Close className="rounded-2xl p-2 hover:bg-slate-100 transition-colors">
                            <X className="size-5 text-slate-400" />
                        </Dialog.Close>
                    </div>

                    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
                        <div>
                            <label className={labelClass}>{t("admin.pricing.fieldName")}</label>
                            <input
                                {...register("name", { required: true })}
                                placeholder={t("admin.pricing.fieldNamePlaceholder")}
                                className={cn(inputClass, errors.name && "border-red-300 focus:ring-red-200")}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>{t("admin.pricing.fieldDescription")}</label>
                            <input
                                {...register("description")}
                                placeholder={t("admin.pricing.fieldDescriptionPlaceholder")}
                                className={inputClass}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>{t("admin.pricing.fieldValidFrom")}</label>
                                <input
                                    type="date"
                                    {...register("validFrom", { required: true })}
                                    className={cn(inputClass, errors.validFrom && "border-red-300 focus:ring-red-200")}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>{t("admin.pricing.fieldValidTo")}</label>
                                <input
                                    type="date"
                                    {...register("validTo", { required: true })}
                                    className={cn(inputClass, errors.validTo && "border-red-300 focus:ring-red-200")}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className={labelClass}>{t("admin.pricing.fieldPricePerNight")}</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...register("pricePerNight", { valueAsNumber: true, required: true, min: 0 })}
                                    className={cn(inputClass, errors.pricePerNight && "border-red-300 focus:ring-red-200")}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>{t("admin.pricing.fieldLocalTax")}</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...register("localTaxPerPersonPerNight", { valueAsNumber: true, min: 0 })}
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>{t("admin.pricing.fieldPetFee")}</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...register("petFeePerNight", { valueAsNumber: true, min: 0 })}
                                    className={inputClass}
                                />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm font-bold text-slate-600 cursor-pointer">
                            <input type="checkbox" {...register("active")} className="rounded accent-brand" />
                            {t("admin.pricing.fieldActive")}
                        </label>

                        <div className="flex items-center justify-end gap-3 pt-2">
                            <Dialog.Close asChild>
                                <button type="button" className="px-5 py-2.5 rounded-[28px] bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-colors">
                                    {t("admin.pricing.cancel")}
                                </button>
                            </Dialog.Close>
                            <button
                                type="submit"
                                disabled={mutation.isPending}
                                className="px-6 py-2.5 rounded-[28px] bg-brand text-white font-black text-sm hover:bg-brand-dark transition-colors active:scale-95 disabled:opacity-50 shadow-lg"
                            >
                                {mutation.isPending ? t("admin.pricing.saving") : t("admin.pricing.save")}
                            </button>
                        </div>
                    </form>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    )
}

export const AdminPricingPage = () => {
    const { t, i18n } = useTranslation()
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"
    const { data: areal, isLoading: arealLoading } = useAdminAreal()
    const queryClient = useQueryClient()
    const [modalOpen, setModalOpen] = useState(false)
    const [editing, setEditing] = useState<PricingResponse | null>(null)

    const queryKey = ["admin", "pricing", areal?.id]

    const { data: priceLists = [], isLoading } = useQuery({
        queryKey,
        queryFn: () => api.get<PricingResponse[]>(`/areals/${areal!.id}/price-lists`),
        enabled: !!areal?.id,
    })

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/areals/${areal!.id}/price-lists/${id}`),
        onSuccess: () => {
            toast.success(t("admin.pricing.toastDeleted"))
            queryClient.invalidateQueries({ queryKey })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.pricing.toastDeleteFailed"))
        },
    })

    if (arealLoading || isLoading) {
        return <div className="text-slate-400 font-bold py-8 text-center">{t("common.loading")}</div>
    }

    if (!areal) {
        return <div className="text-red-500 font-bold py-8 text-center">{t("admin.pricing.noAreal")}</div>
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-slate-900">{t("admin.pricing.title")}</h1>
                    <p className="text-slate-500 font-bold mt-1">{t("admin.pricing.subtitle")}</p>
                </div>
                <button
                    onClick={() => { setEditing(null); setModalOpen(true) }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-brand text-white font-black text-sm hover:bg-brand-dark transition-colors active:scale-95 shadow-xl"
                >
                    <Plus className="size-4" />
                    {t("admin.pricing.addNew")}
                </button>
            </div>

            {priceLists.length === 0 ? (
                <div className="text-center py-16 text-slate-400 font-bold">
                    {t("admin.pricing.empty")}
                </div>
            ) : (
                <div className="grid gap-4">
                    {priceLists.map((pl) => (
                        <div
                            key={pl.id}
                            className="bg-white rounded-[28px] border border-slate-100 shadow-xl p-6"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-1">
                                        <h3 className="text-xl font-black text-slate-900">{pl.name}</h3>
                                        {pl.active && (
                                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-brand/10 border border-brand/30 text-xs font-black text-green-700">
                                                <CheckCircle className="size-3" />
                                                {t("admin.pricing.active")}
                                            </span>
                                        )}
                                    </div>
                                    {pl.description && (
                                        <p className="text-sm font-bold text-slate-500 mb-3">{pl.description}</p>
                                    )}
                                    <div className="flex items-center gap-2 text-sm font-bold text-slate-500 mb-4">
                                        <Calendar className="size-4" />
                                        <span>{formatDate(pl.validFrom, locale)} – {formatDate(pl.validTo, locale)}</span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="bg-slate-50 rounded-2xl p-3">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">
                                                {t("admin.pricing.perNight")}
                                            </p>
                                            <p className="text-lg font-black text-slate-900">
                                                {formatCurrency(pl.pricePerNight, areal.currency, locale)}
                                            </p>
                                        </div>
                                        {pl.localTaxPerPersonPerNight > 0 && (
                                            <div className="bg-slate-50 rounded-2xl p-3">
                                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">
                                                    {t("admin.pricing.localTax")}
                                                </p>
                                                <p className="text-lg font-black text-slate-900">
                                                    {formatCurrency(pl.localTaxPerPersonPerNight, areal.currency, locale)}
                                                </p>
                                            </div>
                                        )}
                                        {pl.petFeePerNight > 0 && (
                                            <div className="bg-slate-50 rounded-2xl p-3">
                                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">
                                                    {t("admin.pricing.petFee")}
                                                </p>
                                                <p className="text-lg font-black text-slate-900">
                                                    {formatCurrency(pl.petFeePerNight, areal.currency, locale)}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                    <button
                                        onClick={() => { setEditing(pl); setModalOpen(true) }}
                                        className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                                        title={t("admin.pricing.edit")}
                                    >
                                        <Pencil className="size-4" />
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (window.confirm(t("admin.pricing.confirmDelete"))) {
                                                deleteMutation.mutate(pl.id)
                                            }
                                        }}
                                        className="p-2 rounded-xl hover:bg-red-50 transition-colors text-slate-400 hover:text-red-500"
                                        title={t("admin.pricing.delete")}
                                    >
                                        <Trash2 className="size-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {modalOpen && (
                <PricingFormModal
                    open={modalOpen}
                    onClose={() => { setModalOpen(false); setEditing(null) }}
                    editing={editing}
                    arealId={areal.id}
                    queryKey={queryKey}
                />
            )}
        </div>
    )
}
